/**
 * src/utils/portalLinkToken.js
 *
 * Reads a portal access token out of the URL FRAGMENT, and removes it from the
 * address bar immediately.
 *
 * WHY A FRAGMENT AND NOT A QUERY PARAMETER
 *
 * A fragment is never sent to the server. A query parameter is, and a portal
 * token IS the credential — there is no session behind it, so anyone holding
 * the URL holds the access. Sent as `?token=`, that credential reaches:
 *
 *   • the Cloudflare Worker every *.tryggr.dk request passes through,
 *   • any intermediary or reverse proxy between the recipient and us,
 *   • the `Referer` header on the next navigation the page makes,
 *   • browser history,
 *   • and the RECIPIENT ORGANISATION's own proxy and DLP logs — and portal
 *     recipients are candidates, employees, suppliers and vendors, so those
 *     intermediaries belong to neither us nor the tenant.
 *
 * The product already knew this: `magic-link-issue` has always used
 * `#mltoken=`, and `GrcClientPortal` reads it from the hash and calls
 * `history.replaceState` straight away. This module is that pattern, extracted
 * so the other portals share one implementation rather than three copies.
 *
 * GH #6767. The founder chose the straight cutover (option B) over a
 * dual-accept transition: `?token=` is NOT read, by design. Every unexpired
 * link already in a recipient's inbox stops working on deploy and the person
 * must be sent a new one. That is a deliberate, known cost — do not "helpfully"
 * add a query-string fallback back in, because a fallback that is never removed
 * is the same disclosure with a longer timeline.
 */

/**
 * Pull the token out of `window.location.hash` and scrub it from the URL.
 *
 * Scrubbing happens BEFORE the caller does anything with the token, so a
 * verification request that redirects, a screenshot, a shoulder-surfer or a
 * user pasting "the page I'm on" into a support ticket never sees it. The
 * fragment is not in the request anyway; this is about everything downstream
 * of the browser.
 *
 * @param {string} [name='token'] — fragment key, e.g. `#token=…`
 * @returns {string|null}
 */
export function readPortalTokenFromFragment(name = 'token') {
  if (typeof window === 'undefined') return null;

  const hash = window.location.hash || '';
  // Matches `#token=…` and `#a=1&token=…`. Bounded to the fragment's own
  // separators so a value cannot swallow the rest of the hash.
  const match = hash.match(new RegExp(`[#&]${name}=([^&]+)`));
  if (!match) return null;

  let token;
  try {
    token = decodeURIComponent(match[1]);
  } catch {
    token = match[1]; // a token that is not valid percent-encoding is still a token
  }

  try {
    window.history.replaceState(
      null,
      '',
      window.location.pathname + (window.location.search || '')
    );
  } catch {
    /* replaceState can throw in a sandboxed frame — the token is still returned */
  }

  return token || null;
}

/**
 * The message shown when no token is present.
 *
 * Its own function because after this cutover the most likely reason is not a
 * malformed link but an OLD one: a `?token=` link sent before the change,
 * which this deliberately does not read. "No token found" would send that
 * person to check their email again and find the same dead link.
 */
export function missingPortalTokenMessage(thing = 'link') {
  return (
    `This ${thing} is missing its access token, or it was sent before ` +
    'we changed how these links are built. Please ask for a new one.'
  );
}
